import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { ArrowBack, AccountTree } from "@mui/icons-material";
import {
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { brandPalette as primaryBlue } from "../config/brandTheme";
import {
  AUTO_FILL_MAX_LEVEL,
  getLevelAmount,
  getPublicAfpSerial,
} from "../config/autoFillProgramConfig";
import {
  AUTO_FILL_LEVEL_FORFEITURE_MONTHS,
  forfeitureLevelsInRange,
} from "../config/autoFillLevelForfeitureConfig";
import { getLevelForfeitureState } from "../services/autoFill/autoFillLevelForfeitureTracking";
import { countQualifiedDirectReferralsForWithdrawal, countQualifiedDirectReferrals } from "../services/autoFill/autoFillQualification";
import { getAutoFillCoPointsWallet, computeProgramCoPointsWallet } from "../utils/autoFillCoPointsWallet";
import AutoFillProgramService from "../services/autoFill/AutoFillProgramService";
import { maybeAdvanceProgramFromUpgradePool } from "../services/autoFill/autoFillEngine";
import { acceptAutoFillWithdrawalTerms } from "../services/autoFill/autoFillWithdrawalUserActions";
import { buildPerLevelWithdrawalDialogModel } from "../services/autoFill/autoFillWithdrawalSummary";
import AutoFillWithdrawalDialog from "./AutoFillWithdrawalDialog";
import {
  isRootAutoFillProgram,
  sortProgramsForDisplay,
  usesSharedMemberPointsWalletForProgram,
} from "../services/autoFill/autoFillProgramSpawn";
import { AUTO_FILL_MAX_CHILD_PROGRAMS_PER_ROOT } from "../config/autoFillProgramConfig";
import {
  formatAutoFillLedgerPrimary,
  formatAutoFillLedgerSecondary,
} from "../utils/autoFillLedgerDisplay";

function matrixPayIndexByPositionId(positions, level) {
  const filled = positions
    .filter(
      (p) => Number(p.level) === Number(level) && p.positionStatus === "filled"
    )
    .sort(
      (a, b) =>
        new Date(a.filledAt || 0).getTime() - new Date(b.filledAt || 0).getTime()
    );
  const map = {};
  filled.forEach((p, i) => {
    map[p.id] = i + 1;
  });
  return map;
}

function filledMatrixCountByLevel(positions) {
  const counts = {};
  for (const p of positions || []) {
    if (p.positionStatus !== "filled") continue;
    const L = Number(p.level);
    counts[L] = (counts[L] || 0) + 1;
  }
  return counts;
}

function describeMatrixPayIndex(payIdx, level) {
  const unit = getLevelAmount(level);
  if (payIdx === 1) {
    return { label: "Pay #1", detail: `₹${unit} → CO Points wallet (withdrawal tranche)` };
  }
  if (payIdx === 2) {
    return { label: "Pay #2", detail: `₹${unit} → Upgrade pool (reserved)` };
  }
  if (payIdx === 3) {
    return {
      label: "Pay #3",
      detail: `₹${unit} → Upgrade pool · with pay #2 = ₹${unit * 2} unlocks Level ${level + 1}`,
    };
  }
  return { label: "—", detail: "Open slot" };
}

const AutoFillProgramDashboard = ({
  user,
  userProfile,
  onBack,
  backLabel = "Back",
}) => {
  const [loading, setLoading] = useState(true);
  const [program, setProgram] = useState(null);
  const [allPrograms, setAllPrograms] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [directCount, setDirectCount] = useState(0);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [withdrawModel, setWithdrawModel] = useState(null);
  const [submittingWithdrawLevel, setSubmittingWithdrawLevel] = useState(null);
  const [withdrawSubmitError, setWithdrawSubmitError] = useState("");
  const [allLedger, setAllLedger] = useState([]);
  const [allWithdrawals, setAllWithdrawals] = useState([]);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [userDocId, setUserDocId] = useState(null);
  const [matrixPositions, setMatrixPositions] = useState([]);
  const globalWallet = getAutoFillCoPointsWallet(userProfile);

  const matrixFilledByLevel = useMemo(
    () => filledMatrixCountByLevel(matrixPositions),
    [matrixPositions]
  );

  const programWallet = useMemo(() => {
    if (!program || !user?.uid) {
      return { available: 0, earned: 0, withdrawn: 0, locked: 0 };
    }
    const includeCo =
      isRootAutoFillProgram(program) && Number(program.enrollmentSeq) === 1;
    return computeProgramCoPointsWallet(
      program,
      allLedger,
      user.uid,
      allWithdrawals,
      {
        includeGenesisAdminCo: includeCo,
        userCoBucket: includeCo ? globalWallet.autoFillCoPoints : null,
        memberPointsWallet: usesSharedMemberPointsWalletForProgram(program)
          ? userProfile?.autoFillPoints
          : null,
        filledMatrixCountByLevel: matrixFilledByLevel,
      }
    );
  }, [
    program,
    allLedger,
    allWithdrawals,
    user?.uid,
    globalWallet.autoFillCoPoints,
    userProfile?.autoFillPoints,
    matrixFilledByLevel,
  ]);

  const points = programWallet;

  const load = async () => {
    if (!user?.uid) return;
    setLoading(true);
    setError("");
    try {
      const progSnap = await getDocs(
        query(collection(db, "autoFillPrograms"), where("userId", "==", user.uid))
      );
      const progs = sortProgramsForDisplay(
        progSnap.docs.map((d) => ({ id: d.id, ...d.data() }))
      );
      for (const p of progs) {
        try {
          await maybeAdvanceProgramFromUpgradePool(p.id, p);
        } catch (advanceErr) {
          console.warn("Auto Fill level advance check:", p.programId, advanceErr);
        }
      }
      const progSnap2 = await getDocs(
        query(collection(db, "autoFillPrograms"), where("userId", "==", user.uid))
      );
      const progsFresh = sortProgramsForDisplay(
        progSnap2.docs.map((d) => ({ id: d.id, ...d.data() }))
      );
      setAllPrograms(progsFresh);
      setProgram((prev) => {
        if (prev?.programId) {
          const keep = progsFresh.find((p) => p.programId === prev.programId);
          if (keep) return keep;
        }
        return progsFresh[0] || null;
      });

      const ledgerSnap = await getDocs(
        query(collection(db, "autoFillLedger"), where("userId", "==", user.uid))
      );
      const ledgerRows = ledgerSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setAllLedger(ledgerRows);

      const wdSnap = await getDocs(
        query(
          collection(db, "autoFillWithdrawals"),
          where("userId", "==", user.uid),
          where("walletType", "==", "POINTS")
        )
      );
      setAllWithdrawals(wdSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

      const userQ = await getDocs(
        query(collection(db, "users"), where("uid", "==", user.uid))
      );
      if (!userQ.empty) {
        setUserDocId(userQ.docs[0].id);
      }
    } catch (e) {
      console.error(e);
      setError(e.message || "Failed to load Auto Fill program");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [user?.uid]);

  const refreshProgramContext = useCallback(async () => {
    if (!program || !userDocId) return;
    const programLedger = allLedger.filter(
      (r) => r.programId === program.programId
    );
    setLedger(programLedger.slice(0, 20));
    const level = program.currentLevel || 1;
    const n = isRootAutoFillProgram(program)
      ? await countQualifiedDirectReferralsForWithdrawal(userDocId, level)
      : await countQualifiedDirectReferrals(userDocId);
    setDirectCount(n);
  }, [program, userDocId, allLedger]);

  useEffect(() => {
    refreshProgramContext();
  }, [refreshProgramContext]);

  useEffect(() => {
    if (!program?.id) return;
    (async () => {
      const posSnap = await getDocs(
        query(
          collection(db, "autoFillMatrixPositions"),
          where("programDocId", "==", program.id)
        )
      );
      setMatrixPositions(posSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    })();
  }, [program?.id, program?.currentLevel]);

  const levelMatrixSummary = useMemo(() => {
    if (!program) return null;
    const L = program.currentLevel || 1;
    const payMap = matrixPayIndexByPositionId(matrixPositions, L);
    const slots = [1, 2, 3].map((positionIndex) => {
      const pos = matrixPositions.find(
        (p) => Number(p.level) === L && Number(p.positionIndex) === positionIndex
      );
      const filled = pos?.positionStatus === "filled";
      const payIdx = filled ? payMap[pos.id] : null;
      const desc = payIdx ? describeMatrixPayIndex(payIdx, L) : describeMatrixPayIndex(null, L);
      return { positionIndex, filled, payIdx, filler: pos?.filledByProgramId, ...desc };
    });
    const filledCount = slots.filter((s) => s.filled).length;
    const unlockCost = getLevelAmount(L) * 2;
    const reserved = Number(userProfile?.autoFillUpgrade?.reserved) || 0;
    const canUnlock =
      filledCount >= 3 && reserved >= unlockCost - 0.001 && L < AUTO_FILL_MAX_LEVEL;
    return { level: L, slots, filledCount, unlockCost, canUnlock };
  }, [program, matrixPositions, userProfile?.autoFillUpgrade?.reserved]);

  const openWithdrawDialog = async () => {
    if (!user?.uid || !userProfile || !program) return;
    setTermsAccepted(userProfile?.autoFillTermsAccepted === true);
    setWithdrawModel(null);
    setWithdrawSubmitError("");
    setWithdrawOpen(true);
    setError("");
    try {
      let docId = userDocId;
      if (!docId) {
        const userQ = await getDocs(
          query(collection(db, "users"), where("uid", "==", user.uid))
        );
        if (!userQ.empty) docId = userQ.docs[0].id;
        setUserDocId(docId);
      }
      const el = await buildPerLevelWithdrawalDialogModel({
        program,
        pointsWallet: programWallet,
        ledgerRows: allLedger.filter((r) => r.programId === program.programId),
        userId: user.uid,
        userProfile,
        userDocId: docId,
        filledMatrixCountByLevel: matrixFilledByLevel,
        pointsWithdrawals: allWithdrawals.filter(
          (w) => w.walletType === "POINTS"
        ),
      });
      setWithdrawModel(el);
    } catch (e) {
      console.error(e);
      setError(e.message || "Could not load withdrawal details");
      setWithdrawOpen(false);
    }
  };

  const handleWithdrawLevel = async (level, amount) => {
    const amt = Number(amount);
    if (!amt || amt <= 0) {
      setError("Invalid withdrawal amount");
      return;
    }
    if (amt > (withdrawModel?.available ?? points.available ?? 0)) {
      setError("Insufficient points wallet balance");
      return;
    }
    if (!termsAccepted) {
      setError("Please accept the withdrawal terms in the dialog.");
      return;
    }
    const slot = withdrawModel?.levelSlots?.find((s) => s.level === level);
    if (!slot?.canWithdraw) {
      setError(
        slot?.blockers?.[0]?.reason ||
          "You do not meet the requirements for this level tranche."
      );
      return;
    }
    try {
      setSubmittingWithdrawLevel(level);
      setWithdrawSubmitError("");
      if (
        termsAccepted &&
        userProfile?.autoFillTermsAccepted !== true
      ) {
        await acceptAutoFillWithdrawalTerms(user.uid);
      }
      await AutoFillProgramService.requestPointsWithdrawal(
        user.uid,
        amt,
        userProfile?.email,
        userProfile?.name,
        program?.programId,
        level
      );
      setSuccess(
        `Level ${level} withdrawal (₹${amt.toLocaleString("en-IN")}) submitted for admin approval.`
      );
      setWithdrawOpen(false);
      load();
    } catch (e) {
      const msg = e.message || "Withdrawal failed";
      setWithdrawSubmitError(msg);
      setError(msg);
    } finally {
      setSubmittingWithdrawLevel(null);
    }
  };

  const level = program?.currentLevel || 0;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: primaryBlue[50],
        py: 3,
        userSelect: "text",
        WebkitUserSelect: "text",
      }}
    >
      <Box sx={{ maxWidth: 960, mx: "auto", px: 2 }}>
        <Button startIcon={<ArrowBack />} onClick={onBack} sx={{ mb: 2 }}>
          {backLabel}
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
          <AccountTree sx={{ mr: 1, verticalAlign: "middle" }} />
          My Auto-Fill Program
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
            {success}
          </Alert>
        )}

        {loading ? (
          <CircularProgress />
        ) : !program ? (
          <Alert severity="info">
            No Auto Fill program yet. Join via the ₹150 Auto Fill program (legacy Ultra
            members) to receive your Program ID.
          </Alert>
        ) : (
          <>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography color="text.secondary">Program ID</Typography>
                  {allPrograms.length > 1 ? (
                    <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
                      <InputLabel id="afp-program-select-label">Select program</InputLabel>
                      <Select
                        labelId="afp-program-select-label"
                        label="Select program"
                        value={program.programId}
                        onChange={(e) => {
                          const next = allPrograms.find(
                            (p) => p.programId === e.target.value
                          );
                          if (next) setProgram(next);
                        }}
                      >
                        {allPrograms.map((p) => (
                          <MenuItem key={p.programId} value={p.programId}>
                            {p.programId} · AFP ID #{getPublicAfpSerial(p.enrollmentSeq)}
                            {isRootAutoFillProgram(p) ? " (parent)" : " (child)"}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <Typography variant="h6" sx={{ fontFamily: "monospace" }}>
                      {program.programId}
                    </Typography>
                  )}
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                    AFP ID #{getPublicAfpSerial(program.enrollmentSeq)}
                    {program.enrollmentSeq === 1 ? " (company genesis)" : ""}
                    {isRootAutoFillProgram(program)
                      ? ` · up to ${AUTO_FILL_MAX_CHILD_PROGRAMS_PER_ROOT} child serials on approved level 2+ CO withdrawals (₹300+)`
                      : " · child AFP · withdrawals use child T&C (direct + team by level)"}
                  </Typography>
                  {allPrograms.length > 1 && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      Points wallet below is for the selected Program ID only.
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography color="text.secondary">Package</Typography>
                  <Typography variant="h6">₹{program.packageAmount}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography color="text.secondary">Status</Typography>
                  <Chip
                    label={(program.status || "active").toUpperCase()}
                    color={program.status === "on_hold" ? "warning" : "success"}
                  />
                  {program.holdReason && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {program.holdReason}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography color="text.secondary">Current level</Typography>
                  <Typography variant="h6">
                    {level} / {AUTO_FILL_MAX_LEVEL}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={(level / AUTO_FILL_MAX_LEVEL) * 100}
                    sx={{ mt: 1 }}
                  />
                </Grid>
              </Grid>
            </Paper>

            {forfeitureLevelsInRange(level, program).map((L) => {
              const st = getLevelForfeitureState(program, L);
              const summary = st?.forfeitureSummary;
              if (!summary) return null;
              return (
                <Alert key={`forfeit-done-${L}`} severity="error" sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Level {L} CO Points removed
                  </Typography>
                  <Typography variant="body2">{summary.reason}</Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Removed: ₹
                    {(summary.totalRemovedFromCoPoints || 0).toLocaleString("en-IN")}.
                    Paid to next AFP serials: ₹
                    {(summary.totalDistributed || 0).toLocaleString("en-IN")}.
                    {(summary.pendingDistribution || 0) > 0 && (
                      <>
                        {" "}
                        Queued: ₹
                        {summary.pendingDistribution.toLocaleString("en-IN")} (
                        {summary.pendingSlots} × ₹150) — next AFP #
                        {summary.nextAfpEnrollmentSeq}.
                      </>
                    )}
                  </Typography>
                </Alert>
              );
            })}

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2">
                    Points wallet (CO Points)
                    {allPrograms.length > 1 ? ` · ${program.programId}` : ""}
                  </Typography>
                  {program.parentProgramId && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      Child of {program.parentProgramId}
                      {program.spawnedFromWithdrawal ? " (withdrawal spawn)" : ""}
                    </Typography>
                  )}
                  <Typography variant="h5">₹{(points.available || 0).toFixed(2)}</Typography>
                  {(points.locked || 0) > 0 && (
                    <Typography variant="body2" color="warning.main" sx={{ mt: 0.5 }}>
                      ₹{(points.locked || 0).toFixed(2)} awaiting admin withdrawal approval
                      (included in earned, not lost).
                    </Typography>
                  )}
                  <Typography variant="caption" display="block">
                    Earned: ₹{(points.earned || 0).toFixed(2)} · Withdrawn: ₹
                    {(points.withdrawn || 0).toFixed(2)}
                    {(points.locked || 0) > 0
                      ? ` · Pending withdrawal: ₹${(points.locked || 0).toFixed(2)}`
                      : ""}
                  </Typography>
                  {(globalWallet.autoFillCoPoints.available || 0) > 0 &&
                    isRootAutoFillProgram(program) &&
                    Number(program.enrollmentSeq) === 1 && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      Includes ₹{globalWallet.autoFillCoPoints.available.toFixed(2)} admin
                      CO bucket (genesis activations & L1 upline).
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    Each level: 1st matrix payment → withdrawal; 2nd → reserved in upgrade
                    pool; 3rd + reserved unlocks the next level (e.g. ₹150 + ₹150 = ₹300).
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    Each level has its own {AUTO_FILL_LEVEL_FORFEITURE_MONTHS}-month withdrawal
                    window (date &amp; time) inside Request withdrawal.
                  </Typography>
                  <Button
                    size="small"
                    sx={{ mt: 1 }}
                    variant="outlined"
                    onClick={openWithdrawDialog}
                    disabled={(points.available || 0) <= 0}
                  >
                    Request withdrawal
                  </Button>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2">Upgrade pool</Typography>
                  <Typography variant="h5">
                    ₹{(userProfile?.autoFillUpgrade?.reserved || 0).toFixed(2)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Payment 2 of 3 at your level is held here; payment 3 adds the same
                    amount and unlocks the next level (2× level payment).
                    {(userProfile?.autoFillUpgrade?.used || 0) > 0
                      ? ` · Used for unlocks: ₹${(userProfile.autoFillUpgrade.used).toFixed(2)}`
                      : ""}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2">
                    {isRootAutoFillProgram(program) &&
                    (program.currentLevel || 1) >= 6
                      ? "Direct Ultra members"
                      : "Direct referrals (active AFP)"}
                  </Typography>
                  <Typography variant="h5">{directCount}</Typography>
                  <Typography variant="caption">
                    {isRootAutoFillProgram(program)
                      ? "Counts for withdrawal rules on the selected program."
                      : "Child AFP: active ₹150 Auto Fill in your direct line."}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            {levelMatrixSummary && (
              <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Level {levelMatrixSummary.level} matrix cycle (
                  {levelMatrixSummary.filledCount}/3)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Only <strong>payment #1</strong> of each level goes to your CO Points wallet.
                  Payments <strong>#2 and #3</strong> go to the upgrade pool; together they unlock
                  the next level (2× ₹{getLevelAmount(levelMatrixSummary.level)} = ₹
                  {levelMatrixSummary.unlockCost.toLocaleString("en-IN")}).
                </Typography>
                <List dense disablePadding>
                  {levelMatrixSummary.slots.map((slot) => (
                    <ListItem key={slot.positionIndex} disableGutters>
                      <ListItemText
                        primary={`Slot ${slot.positionIndex} · ${slot.label}${
                          slot.filled ? " · filled" : " · waiting"
                        }`}
                        secondary={
                          slot.filled
                            ? `${slot.detail}${
                                slot.filler ? ` · from ${slot.filler}` : ""
                              }`
                            : slot.detail
                        }
                      />
                    </ListItem>
                  ))}
                </List>
                {levelMatrixSummary.canUnlock && (
                  <Alert severity="success" sx={{ mt: 1 }}>
                    L{levelMatrixSummary.level} cycle complete and upgrade pool has ₹
                    {levelMatrixSummary.unlockCost.toLocaleString("en-IN")} — Level{" "}
                    {levelMatrixSummary.level + 1} can unlock (refresh if level still shows{" "}
                    {levelMatrixSummary.level}).
                  </Alert>
                )}
              </Paper>
            )}

            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Recent ledger
              </Typography>
              <List dense>
                {ledger.length === 0 ? (
                  <ListItem>
                    <ListItemText primary="No transactions yet" />
                  </ListItem>
                ) : (
                  ledger.map((row) => (
                    <ListItem key={row.id}>
                      <ListItemText
                        primary={formatAutoFillLedgerPrimary(row)}
                        secondary={formatAutoFillLedgerSecondary(row)}
                      />
                    </ListItem>
                  ))
                )}
              </List>
            </Paper>
          </>
        )}

        <AutoFillWithdrawalDialog
          open={withdrawOpen}
          onClose={() => setWithdrawOpen(false)}
          model={withdrawModel}
          termsAccepted={termsAccepted}
          onTermsAcceptedChange={setTermsAccepted}
          onRequestLevel={handleWithdrawLevel}
          submittingLevel={submittingWithdrawLevel}
          submitError={withdrawSubmitError}
        />
      </Box>
    </Box>
  );
};

export default AutoFillProgramDashboard;
